import { useEffect, useRef, useState } from 'react';
import {
  STATUS_POOLS,
  STATUS_NOTE_TYPES,
  STATUS_COLOR_PALETTE,
  type ContactStatusRow,
  type StatusMode,
  type StatusNoteType,
  type StatusPool,
} from '@ehr/shared';
import { MultiDd, type DdOption } from '../components/Dropdown.js';
import { api, ApiError } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';

/**
 * Statuses manager (V38 Settings → Statuses). A per-pool, ordered list of
 * configurable statuses. Contact Type switcher (7 pools) + New Status / Saved
 * Statuses tabs; each status has a label, behaviour (counter/flag), a logged
 * activity type, and a colour. The system "New" status is name-locked. 1:1 with
 * the mockup's renderStatusManager; CRUD against /contact-statuses.
 */

type Draft = { label: string; color: string; mode: StatusMode; noteType: StatusNoteType };
// Flag is the default behaviour; Counter is currently disabled (see the toggle).
const BLANK: Draft = { label: '', color: '#1a8a66', mode: 'flag', noteType: 'call' };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const isHex = (s: string) => /^#[0-9a-fA-F]{6}$/.test(s);
const POOL_OPTIONS: DdOption[] = STATUS_POOLS.map((p) => ({ value: p.key, label: p.label }));
const poolLabel = (p: StatusPool) => STATUS_POOLS.find((x) => x.key === p)?.label ?? p;
const HANDLE = '⁙⁙';

const Pencil = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const Trash = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

/** The shared status form body (Label / Behavior / Activity / Color). */
function StatusFormFields({
  draft,
  setDraft,
  systemLocked,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  systemLocked?: boolean;
}) {
  return (
    <div className="status-mgr-form-grid">
      <div className="field field-full">
        <label>Label</label>
        {systemLocked ? (
          <>
            <input type="text" value={draft.label} disabled />
            <div className="ctf-name-note">
              System status — the name is locked. You can recolor or delete it, but not rename it.
            </div>
          </>
        ) : (
          <input
            type="text"
            value={draft.label}
            placeholder="e.g. Attempted, Left Voicemail, Demo Scheduled"
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          />
        )}
      </div>
      <div className="field field-full">
        <label>Behavior</label>
        <div className="status-mgr-type-pills">
          <button
            type="button"
            className={`status-mgr-type-pill ${draft.mode === 'counter' ? 'active' : ''}`}
            disabled
            title="Counter behaviour is currently disabled"
          >
            Counter (auto-numbered)
          </button>
          <button
            type="button"
            className={`status-mgr-type-pill ${draft.mode === 'flag' ? 'active' : ''}`}
            onClick={() => setDraft({ ...draft, mode: 'flag' })}
          >
            Flag (single label)
          </button>
        </div>
      </div>
      <div className="field field-full">
        <label>Log as activity type</label>
        <div className="status-mgr-type-pills">
          {STATUS_NOTE_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={`status-mgr-type-pill ${draft.noteType === t ? 'active' : ''}`}
              onClick={() => setDraft({ ...draft, noteType: t })}
            >
              {cap(t)}
            </button>
          ))}
        </div>
      </div>
      <div className="field field-full">
        <label>Color</label>
        <div className="cp-grid cp-grid-sm">
          {STATUS_COLOR_PALETTE.map((c, i) => (
            <button
              key={c + i}
              type="button"
              className={`cp-sw${draft.color.toLowerCase() === c.toLowerCase() ? ' sel' : ''}`}
              style={{ background: c }}
              title={c}
              onClick={() => setDraft({ ...draft, color: c })}
            />
          ))}
        </div>
        <div className="cp-custom" style={{ marginTop: 9 }}>
          <label
            className="cp-native"
            style={{ background: draft.color || '#1a8a66' }}
            title="Spectrum"
          >
            <input
              type="color"
              value={isHex(draft.color) ? draft.color : '#1a8a66'}
              onChange={(e) => setDraft({ ...draft, color: e.target.value })}
            />
          </label>
          <input
            className="cp-hex"
            value={draft.color}
            maxLength={7}
            placeholder="#1a8a66"
            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

export function StatusesManager({ onClose }: { onClose: () => void }) {
  // Multi-select Contact Types: the Saved list combines every selected pool, and
  // "Add status" creates the status in each of them at once. Each row carries its
  // own `.pool`, so edit/delete/reorder still act on the one pool it belongs to.
  const [pools, setPools] = useState<StatusPool[]>(['patient']);
  const [tab, setTab] = useState<'new' | 'saved'>('new');
  const [statuses, setStatuses] = useState<ContactStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [newDraft, setNewDraft] = useState<Draft>(BLANK);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(BLANK);
  const [q, setQ] = useState('');
  const dragId = useRef<string | null>(null);
  const multi = pools.length > 1;

  const load = (poolList: StatusPool[]) => {
    if (poolList.length === 0) {
      setStatuses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(
      poolList.map((p) =>
        api
          .get<ContactStatusRow[]>(`/contact-statuses?pool=${p}`)
          .catch(() => [] as ContactStatusRow[]),
      ),
    )
      .then((results) => {
        // Concat in the selected order so each pool's rungs stay grouped + ordered.
        setStatuses(results.flat());
        setErr(null);
      })
      .catch(() => setErr('Could not load statuses. Please try again.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => load(pools), [pools]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const switchPools = (next: string[]) => {
    setEditingId(null);
    setNewDraft(BLANK);
    setQ('');
    setPools(next as StatusPool[]);
  };
  const switchTab = (t: 'new' | 'saved') => {
    setEditingId(null);
    setNewDraft(BLANK);
    setTab(t);
  };

  const failMsg = (e: unknown, fallback: string) =>
    e instanceof ApiError && e.code === 'DUPLICATE'
      ? 'A status with that name already exists in this pool.'
      : e instanceof ApiError && e.code === 'LOCKED'
        ? 'System statuses can’t be renamed.'
        : fallback;

  const addStatus = () => {
    const label = newDraft.label.trim();
    if (!label || pools.length === 0) return;
    if (!isHex(newDraft.color)) {
      setErr('Enter a valid color like #1a8a66.');
      return;
    }
    const body = { label, color: newDraft.color, mode: newDraft.mode, noteType: newDraft.noteType };
    // Create the status in every selected Contact Type; a pool that already has
    // the label rejects (409) — report those, keep the successes.
    Promise.allSettled(pools.map((p) => api.post(`/contact-statuses?pool=${p}`, body))).then(
      (results) => {
        setNewDraft(BLANK);
        load(pools);
        const dupes = pools.filter((_, i) => results[i]!.status === 'rejected');
        setErr(
          dupes.length && dupes.length === pools.length
            ? `A status named “${label}” already exists in ${dupes.map(poolLabel).join(', ')}.`
            : dupes.length
              ? `Added — but “${label}” already existed in ${dupes.map(poolLabel).join(', ')}.`
              : null,
        );
      },
    );
  };

  const startEdit = (s: ContactStatusRow) => {
    setEditingId(s.id);
    setEditDraft({ label: s.label, color: s.color, mode: s.mode, noteType: s.noteType });
    setTab('saved');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(BLANK);
  };
  const saveEdit = (s: ContactStatusRow) => {
    if (!isHex(editDraft.color)) {
      setErr('Enter a valid color like #1a8a66.');
      return;
    }
    const body: Partial<Draft> = {
      color: editDraft.color,
      mode: editDraft.mode,
      noteType: editDraft.noteType,
    };
    if (!s.system) body.label = editDraft.label.trim() || s.label;
    api
      .patch(`/contact-statuses/${s.id}`, body)
      .then(() => {
        cancelEdit();
        load(pools);
      })
      .catch((e) => setErr(failMsg(e, 'Could not save the status.')));
  };
  const del = async (s: ContactStatusRow) => {
    if (!(await askConfirm(`Delete status “${s.label}”?`))) return;
    api
      .del(`/contact-statuses/${s.id}`)
      .then(() => load(pools))
      .catch(() => setErr('Could not delete the status.'));
  };

  // Reorder only makes sense within one pool; disabled while multiple are selected.
  const reorder = (targetId: string) => {
    const dId = dragId.current;
    dragId.current = null;
    if (!dId || dId === targetId || multi || pools.length !== 1) return;
    const next = statuses.slice();
    const di = next.findIndex((s) => s.id === dId);
    if (di < 0) return;
    const [moved] = next.splice(di, 1);
    const ti = next.findIndex((s) => s.id === targetId);
    next.splice(ti < 0 ? next.length : ti, 0, moved!);
    setStatuses(next);
    api
      .patch(`/contact-statuses/reorder?pool=${pools[0]}`, { ids: next.map((s) => s.id) })
      .catch(() => {
        setErr('Could not reorder.');
        load(pools);
      });
  };

  const ql = q.trim().toLowerCase();
  const shown = ql ? statuses.filter((s) => s.label.toLowerCase().includes(ql)) : statuses;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-lg" role="dialog" aria-modal="true" aria-label="Statuses">
        <div className="modal-head">
          <div>
            <h2>Statuses</h2>
            <p>Define the actions you log and the labels they create on your prospects.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: 14 }}>
            <label className="status-mgr-eyebrow">Contact Type</label>
            <MultiDd
              values={pools}
              options={POOL_OPTIONS}
              onChange={switchPools}
              placeholder="— Select contact type(s) —"
              searchable={false}
              selectAll
            />
          </div>

          <div className="status-mgr-tabs">
            <button
              type="button"
              className={`status-mgr-tab${tab === 'new' ? ' active' : ''}`}
              onClick={() => switchTab('new')}
            >
              New Status
            </button>
            <button
              type="button"
              className={`status-mgr-tab${tab === 'saved' ? ' active' : ''}`}
              onClick={() => switchTab('saved')}
            >
              Saved Statuses
              {statuses.length > 0 && (
                <span className="status-mgr-tab-badge">{statuses.length}</span>
              )}
            </button>
          </div>

          {err && <div className="config-err">{err}</div>}

          {tab === 'new' ? (
            <div className="status-mgr-form">
              <StatusFormFields draft={newDraft} setDraft={setNewDraft} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn" onClick={() => setNewDraft(BLANK)}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={addStatus}
                  disabled={!newDraft.label.trim() || pools.length === 0}
                >
                  {multi ? `Add to ${pools.length} contact types` : 'Add status'}
                </button>
              </div>
            </div>
          ) : loading ? (
            <p className="muted">Loading…</p>
          ) : statuses.length === 0 ? (
            <div className="status-mgr-empty">
              No statuses defined yet. Switch to <strong>New Status</strong> to add one.
            </div>
          ) : (
            <>
              <input
                type="text"
                className="status-mgr-search"
                placeholder="Search statuses…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <div className="status-mgr-list">
                {shown.map((s) =>
                  editingId === s.id ? (
                    <div className="status-mgr-form" key={s.id}>
                      <StatusFormFields
                        draft={editDraft}
                        setDraft={setEditDraft}
                        systemLocked={s.system}
                      />
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="btn" onClick={cancelEdit}>
                          Cancel
                        </button>
                        <button className="btn primary" onClick={() => saveEdit(s)}>
                          Save changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="status-mgr-row"
                      key={s.id}
                      draggable={!multi}
                      onDragStart={(e) => {
                        if (multi) return;
                        dragId.current = s.id;
                        e.currentTarget.classList.add('dragging');
                      }}
                      onDragEnd={(e) => e.currentTarget.classList.remove('dragging')}
                      onDragOver={(e) => {
                        if (multi) return;
                        e.preventDefault();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('drag-over');
                        reorder(s.id);
                      }}
                    >
                      <span
                        className="status-mgr-handle"
                        aria-hidden
                        title={multi ? 'Select one contact type to reorder' : 'Drag to reorder'}
                        style={multi ? { opacity: 0.3, cursor: 'default' } : undefined}
                      >
                        {HANDLE}
                      </span>
                      <span className="status-mgr-color" style={{ background: s.color }} />
                      <div className="status-mgr-main">
                        <span className="status-mgr-label">
                          {s.label}
                          {s.system && <span className="status-mgr-sys-tag">system</span>}
                          {multi && (
                            <span className="status-mgr-pool-tag">{poolLabel(s.pool)}</span>
                          )}
                        </span>
                        <span className="status-mgr-sub">
                          {cap(s.mode)} · Logs as {cap(s.noteType)}
                        </span>
                      </div>
                      <div className="status-mgr-actions">
                        <button
                          className="status-mgr-btn"
                          title="Edit"
                          aria-label={`Edit ${s.label}`}
                          onClick={() => startEdit(s)}
                        >
                          {Pencil}
                        </button>
                        <button
                          className="status-mgr-btn danger"
                          title="Delete"
                          aria-label={`Delete ${s.label}`}
                          onClick={() => del(s)}
                        >
                          {Trash}
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
